import { ColorsNextConfig, ColorsOldConfig } from './colors';
import { ThemeConfig } from './theme';

const DEFAULT_COLORS_LEGACY: ColorsOldConfig = {
  'Primary/10': '#E5F0FF',
  'Primary/20': '#CCE0FF',
  'Primary/30': '#A0C6FF',
  'Primary/50': '#1170FF',
  'Primary/70': '#0C4EB3',
  'Primary/80': '#072D66',
  'Primary/90': '#031530',
  'Secondary/10': '#E5FFED',
  'Secondary/20': '#CFFCDC',
  'Secondary/30': '#AAF5BF',
  'Secondary/50': '#21C44F',
  'Secondary/70': '#01922A',
  'Secondary/80': '#0A6123',
  'Secondary/90': '#00330E',
  'Blue/10': '#E5F0FF',
  'Blue/20': '#CCE0FF',
  'Blue/30': '#A0C6FF',
  'Blue/50': '#1170FF',
  'Blue/70': '#0C4EB3',
  'Blue/80': '#072D66',
  'Blue/90': '#031530',
  'Green/10': '#E5FFEA',
  'Green/20': '#CCFFD4',
  'Green/30': '#AAF2B6',
  'Green/50': '#36B24A',
  'Green/70': '#19802A',
  'Green/80': '#084D13',
  'Green/90': '#002606',
  'Neutral/0': '#FFFFFF',
  'Neutral/10': '#F2F8FF',
  'Neutral/20': '#DAE6F2',
  'Neutral/30': '#C3D4E5',
  'Neutral/50': '#ADBDCC',
  'Neutral/70': '#6A7785',
  'Neutral/80': '#3D454D',
  'Neutral/90': '#18191B',
  'Neutral/100': '#000000',
  'Purple/10': '#EEE5FF',
  'Purple/20': '#DDCCFF',
  'Purple/30': '#C3A6FF',
  'Purple/50': '#9059FF',
  'Purple/70': '#5B11EE',
  'Purple/80': '#35009E',
  'Purple/90': '#120330',
  'Red/10': '#FFE5E5',
  'Red/20': '#FFCCCC',
  'Red/30': '#FFA6A6',
  'Red/50': '#CC3D3D',
  'Red/70': '#990F0F',
  'Red/80': '#660B0B',
  'Red/90': '#330000',
  'Teal/10': '#E5FFFF',
  'Teal/20': '#CCFFFF',
  'Teal/30': '#80FFFF',
  'Teal/50': '#14CCCC',
  'Teal/70': '#0F8C8C',
  'Teal/80': '#084D4D',
  'Teal/90': '#031A1A',
  'Yellow/10': '#FFFBE5',
  'Yellow/20': '#FFF7CC',
  'Yellow/30': '#FFF2B2',
  'Yellow/50': '#D9B500',
  'Yellow/70': '#8C7500',
  'Yellow/80': '#594A00',
  'Yellow/90': '#262000',
};

const DEFAULT_COLORS_NEXT: ColorsNextConfig = {
  'brand/default': {
    light: '#1170FF',
    dark: '#2b80ff',
  },
  'brand/surface': {
    light: '#e7f1ff',
    dark: '#113366',
  },
  'brand/accent': {
    light: '#0e5acc',
    dark: '#aaccff',
  },
  'brand/tint': {
    light: '#a0c6ff',
    dark: '#256dd9',
  },
  'neutral/default': {
    light: '#4d4d4d',
    dark: '#e6e6e6',
  },
  'neutral/alt': {
    light: '#ffffff',
    dark: '#ffffff',
  },
  'background/default': {
    light: '#ffffff',
    dark: '#0d0d0d',
  },
  'background/alt': {
    light: '#ffffff',
    dark: '#1a1a1a',
  },
  'background/shadow': {
    light: '#000000',
    dark: '#000000',
  },
  'success/default': {
    light: '#36B24A',
    dark: '#36B24A',
  },
  'error/default': {
    light: '#CC3D3D',
    dark: '#CC3D3D',
  },
};

const DEFAULT_THEME: ThemeConfig = {
  fontFamilies: {
    primary: 'Plus Jakarta Sans',
    secondary: 'Plus Jakarta Sans',
    fallback: 'Plus Jakarta Sans',
  },
  typography: {
    marquee: {
      'font-family': 'primary',
      'font-size': '24px',
      'font-weight': 600,
      'line-height': '1.3',
      'letter-spacing': '-0.01em',
    },
    header: {
      'font-family': 'primary',
      'font-size': '20px',
      'font-weight': 600,
      'line-height': '1.3',
      'letter-spacing': 0,
    },
    label: {
      'font-family': 'primary',
      'font-size': '16px',
      'font-weight': 600,
      'line-height': '1.2',
      'letter-spacing': 0,
    },
    body: {
      'font-family': 'primary',
      'font-size': '16px',
      'font-weight': 500,
      'line-height': '1.5',
      'letter-spacing': 0,
    },
    caption: {
      'font-family': 'primary',
      'font-size': '12px',
      'font-weight': 500,
      'line-height': '18px',
      'letter-spacing': '0.01em',
    },
    postscript: {
      'font-family': 'primary',
      'font-size': '10px',
      'font-weight': 500,
      'line-height': '1.5em',
      'letter-spacing': '0.02em',
    },
  },
  colors: {
    legacy: DEFAULT_COLORS_LEGACY,
    next: DEFAULT_COLORS_NEXT,
    pinwheelBrandColor: {
      legacy: '#1170FF',
      next: {
        light: '#1170FF',
        dark: '#1170FF',
      },
    },
  },
  components: {
    background: {
      default: {
        color: {
          type: 'color',
          legacy: 'Neutral/0',
          next: 'background/default',
        },
      },
    },
    button: {
      primary: {
        color: {
          type: 'color',
          legacy: 'Primary/50',
          next: 'brand/default',
        },
        onColor: {
          type: 'color',
          legacy: 'Neutral/0',
          next: 'neutral/alt',
        },
      },
      secondary: {
        color: {
          type: 'color',
          legacy: 'Secondary/50',
          next: 'success/default',
        },
        onColor: {
          type: 'color',
          legacy: 'Neutral/0',
          next: 'neutral/alt',
        },
      },
      neutral: {
        color: {
          type: 'color',
          legacy: 'Neutral/50',
          next: 'neutral/default',
        },
        onColor: {
          type: 'color',
          legacy: 'Neutral/0',
          next: 'background/default',
        },
      },
    },
    buttonGroup: {
      toggle: {
        baseColor: {
          type: 'color',
          legacy: 'Neutral/0',
          next: 'background/alt',
        },
        baseOnColor: {
          type: 'color',
          legacy: 'Neutral/70',
          next: 'neutral/default',
        },
        selectedColor: {
          type: 'color',
          legacy: 'Primary/50',
          next: 'brand/default',
        },
        selectedOnColor: {
          type: 'color',
          legacy: 'Neutral/0',
          next: 'neutral/alt',
        },
        disabledColor: {
          type: 'color',
          legacy: 'Neutral/50',
          next: 'neutral/default',
          opacityLegacy: 0.2,
          opacityNext: 0.1,
        },
        disabledOnColor: {
          type: 'color',
          legacy: 'Neutral/70',
          next: 'neutral/default',
          opacityLegacy: 0.8,
          opacityNext: 0.6,
        },
      },
    },
    card: {
      simple: {
        borderColor: {
          type: 'color',
          legacy: 'Blue/10',
          next: 'brand/surface',
        },
        backgroundColor: {
          type: 'color',
          legacy: 'Neutral/0',
          next: 'background/alt',
        },
      },
    },
    cardButton: {
      simple: {
        focusColor: {
          type: 'color',
          legacy: 'Primary/50',
          next: 'brand/default',
        },
        disabledColor: {
          type: 'color',
          legacy: 'Neutral/50',
          next: 'neutral/default',
        },
      },
    },
    checkbox: {
      default: {
        baseBackground: {
          type: 'color',
          legacy: 'Neutral/0',
          next: 'background/default',
        },
        selectedBackground: {
          type: 'color',
          legacy: 'Primary/10',
          next: 'brand/default',
          opacityNext: 0.2,
        },
        focusedBackground: {
          type: 'color',
          legacy: 'Neutral/10',
          next: 'brand/default',
          opacityNext: 0.1,
        },
        selectedColor: {
          type: 'color',
          legacy: 'Primary/50',
          next: 'brand/accent',
        },
        selectedCheckColor: {
          type: 'color',
          legacy: 'Neutral/0',
          next: 'background/default',
        },
      },
    },
    circularFrame: {
      default: {
        borderColor: {
          type: 'color',
          legacy: 'Blue/10',
          next: 'brand/surface',
        },
        emptyColor: {
          type: 'color',
          legacy: 'Neutral/20',
          next: 'brand/surface',
          opacityNext: 0.4,
        },
      },
    },
    collapser: {
      default: {
        iconBackgroundOnHoverColor: {
          type: 'color',
          legacy: 'Neutral/30',
          next: 'neutral/default',
          opacityNext: 0.2,
        },
      },
    },
    dotLoader: {
      default: {
        color: {
          type: 'color',
          legacy: 'Primary/50',
          next: 'brand/default',
        },
        lightColor: {
          type: 'color',
          legacy: 'Primary/10',
          next: 'brand/surface',
        },
      },
    },
    input: {
      default: {
        errorColor: {
          type: 'color',
          legacy: 'Red/50',
          next: 'error/default',
        },
        errorMessageColor: {
          type: 'color',
          legacy: 'Red/50',
          next: 'error/default',
        },
        borderColor: {
          type: 'color',
          legacy: 'Neutral/50',
          next: 'neutral/default',
        },
        backgroundColor: {
          type: 'color',
          legacy: 'Neutral/0',
          next: 'background/alt',
        },
        focusColor: {
          type: 'color',
          legacy: 'Primary/50',
          next: 'brand/default',
        },
        iconColor: {
          type: 'color',
          legacy: 'Primary/70',
          next: 'neutral/default',
        },
        textColor: {
          type: 'color',
          legacy: 'Neutral/90',
          next: 'neutral/default',
        },
        placeholderColor: {
          type: 'color',
          legacy: 'Neutral/50',
          next: 'neutral/default',
        },
      },
    },
    label: {
      default: {
        color: {
          type: 'color',
          legacy: 'Neutral/80',
          next: 'neutral/default',
        },
      },
    },
    loading: {
      default: {
        color: {
          type: 'color',
          legacy: 'Primary/50',
          next: 'brand/default',
        },
        backgroundColor: {
          type: 'color',
          legacy: 'Neutral/20',
          next: 'neutral/default',
          opacityNext: 0.2,
        },
      },
    },
    radio: {
      default: {
        selectedBackground: {
          type: 'color',
          legacy: 'Primary/70',
          next: 'brand/surface',
          opacityLegacy: 0.08,
        },
        iconBorder: {
          type: 'color',
          legacy: 'Neutral/30',
          next: 'neutral/default',
          opacityNext: 0.7,
        },
        selected: {
          type: 'color',
          legacy: 'Primary/50',
          next: 'brand/accent',
        },
        radioDotColor: {
          type: 'color',
          legacy: 'Neutral/0',
          next: 'background/default',
        },
      },
    },
    text: {
      default: {
        color: {
          type: 'color',
          legacy: 'Neutral/80',
          next: 'neutral/default',
        },
      },
      light: {
        color: {
          type: 'color',
          legacy: 'Neutral/70',
          next: 'neutral/default',
          opacityNext: 0.9,
        },
      },
      link: {
        color: {
          type: 'color',
          legacy: 'Primary/50',
          next: 'brand/default',
        },
      },
      title: {
        color: {
          type: 'color',
          legacy: 'Primary/70',
          next: 'neutral/default',
        },
      },
    },
  },
  animations: {
    duration: {
      short: 250,
      standard: 500,
      long: 1000,
    },
    delay: {
      short: 250,
      standard: 500,
      long: 1000,
    },
    easing: {
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      sharp: 'cubic-bezier(0.4, 0.0, 0.6, 1)',
    },
  },
  spacing: 8,
};

export default DEFAULT_THEME;
